<template>
  <v-container fluid>
    <!-- HEADER -->
    <v-card class="mb-4 pa-4" elevation="2">
      <v-row align="center" justify="space-between">
        <v-col cols="12" md="6">
          <div class="text-h5 font-weight-bold">
            Loan Inventory
          </div>
          <div v-if="consumerName" class="subtitle-1 grey--text text--darken-1 mt-1">
            {{ consumerName }}
          </div>

          <!-- Receipt Number -->
          <v-chip class="mt-2" color="primary" dark label>
            Receipt No: {{ consumerLoanId }}
          </v-chip>
        </v-col>

        <v-col cols="12" md="6" class="d-flex justify-end">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="grey darken-1"
                dark
                class="mr-2"
                v-bind="attrs"
                v-on="on"
                @click="goBack"
              >
                <v-icon left>
                  mdi-arrow-left
                </v-icon>
                Back
              </v-btn>
            </template>
            <span>Go to Previous Page</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </v-card>

    <!-- MAIN CONTENT -->
    <v-row>
      <!-- ADD SECTION -->
      <v-col
        v-if="authPermissions.has('consumer_loans:create')"
        lg="4"
        cols="12"
      >
        <v-card elevation="2">
          <v-toolbar flat color="primary" dark>
            <v-icon left>
              mdi-plus-circle
            </v-icon>
            Add Loan Item
          </v-toolbar>

          <v-card-text>
            <create-consumer-loan-inventory
              :prop-consumer-id="consumerId"
              :prop-consumer-loan-id="consumerLoanId"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- LIST SECTION -->
      <v-col
        :lg="authPermissions.has('consumer_loans:create') ? 8 : 12"
        cols="12"
      >
        <v-card elevation="2">
          <v-toolbar flat color="primary" dark>
            <v-icon left>
              mdi-format-list-bulleted
            </v-icon>
            Loan List
            <v-spacer />

            <v-btn
              v-if="authPermissions.has('consumer_loans:create')"
              color="white"
              text
              :disabled="!computedTempList.length"
              :loading="saving"
              @click="save"
            >
              <v-icon left>
                mdi-content-save
              </v-icon>
              Save
            </v-btn>
          </v-toolbar>

          <!-- Errors -->
          <v-card-text v-if="errors.length">
            <v-alert
              v-for="(item, index) in errors"
              :key="index"
              dense
              type="error"
              outlined
              dismissible
              class="mb-2"
            >
              {{ item[0] }}
            </v-alert>
          </v-card-text>

          <v-card-text>
            <index-consumer-loan-inventory
              :prop-consumer-id="consumerId"
              :prop-consumer-loan-id="consumerLoanId"
              @edit="initEditConsumerLoanInventory"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- EDIT DIALOG -->
    <v-dialog
      v-model="showConsumerLoanInventoryEdit"
      max-width="700px"
      persistent
    >
      <v-card>
        <v-toolbar flat color="primary" dark>
          <v-icon left>
            mdi-pencil
          </v-icon>
          Edit Loan Item
          <v-spacer />
          <v-btn icon @click="showConsumerLoanInventoryEdit = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text>
          <edit-consumer-loan-inventory
            :prop-consumer-id="consumerId"
            :prop-consumer-loan-id="consumerLoanId"
            :prop-consumer-loan-inventory="consumerLoanInventory"
            @close-dialog="showConsumerLoanInventoryEdit = false"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<style scoped>
.v-card {
  border-radius: 12px;
}

.v-toolbar {
  font-weight: 600;
  letter-spacing: 0.5px;
}

.text-h5 {
  letter-spacing: 0.5px;
}
</style>
<script>
import { mapState, mapActions } from 'vuex'
import consumerLoanInventoryRepository from '@/repositories/consumer-loan-inventory.repository'
import consumerRepository from '@/repositories/consumer.repository'
import { errorHandler } from '@/extra-functions/store.extra'
export default {
  name: 'ConsumerLoanInventory',
  components: {
    indexConsumerLoanInventory: () =>
      import('@/views/dashboard/components/consumer-loan-inventories/Index'),
    createConsumerLoanInventory: () =>
      import('@/views/dashboard/components/consumer-loan-inventories/Create'),
    editConsumerLoanInventory: () =>
      import('@/views/dashboard/components/consumer-loan-inventories/Edit'),
  },
  data() {
    return {
      consumerId: this.$route.params.consumer,
      consumerLoanId: this.$route.params.consumerLoan,
      consumerName: '',
      consumerLoanInventory: {},
      showConsumerLoanInventoryEdit: false,
      saving: false,
      errors: [],
    }
  },
  created() {
    consumerRepository.show(this.consumerId).then(({ data }) => {
      this.consumerName = data.name
    })
  },
  computed: {
    ...mapState({
      loanTempList: (state) => state.consumerLoanInventory.tempList,
      authPermissions: (state) => state.auth.userPermissions,
    }),
    computedTempList() {
      return this.loanTempList.filter(
        (e) => e.consumer_loan_id === this.consumerLoanId,
      )
    },
  },
  methods: {
    ...mapActions({
      clearTempList: 'consumerLoanInventory/clearTempList',
    }),
    initEditConsumerLoanInventory(data) {
      this.consumerLoanInventory = data
      this.showConsumerLoanInventoryEdit = true
    },
    save() {
      if (!this.saving) {
        this.saving = true
        this.errors = []
        const params = {
          consumer: this.consumerId,
          consumerLoan: this.consumerLoanId,
        }
        consumerLoanInventoryRepository
          .store(params, this.computedTempList)
          .then((r) => {
            this.clearTempList(this.consumerLoanId)
          })
          .catch((e) => {
            this.errors = errorHandler(e)
          })
          .then(() => {
            this.saving = false
          })
      }
    },
    goBack() {
      const to = this.$route.query.callback ?? -1
      if (to !== -1) {
        this.$router.push(to)
        return
      }
      this.$router.go(-1)
    },
  },
}
</script>
